<template>
  <div class="container">
    <h1 class="header-title mb-4">Personal Bank Account</h1>

    <!-- Bank Account Section -->
    <Card class="profile-card">
      <template #header>
        <div class="card-header">
          <i class="fa-solid fa-building-columns mr-2"></i>Bank Account
        </div>
      </template>
      <template #content>
        <div class="card-content">
          <p class="subtitle mb-4">
            Specify Your Bank Account for Reward Payments
          </p>
          <div
            class="form-row mb-3"
          >
            <label class="td-title">Account Type</label>
            <div class="p-inputgroup">
              <div class="d-flex flex-column w-100">
                <div class="p-float-label">
                  <Dropdown
                    v-model="formData.accountType"
                    :options="accountTypes"
                    optionLabel="name"
                    optionValue="value"
                    class="input-class"
                    :class="{ 'p-invalid': v$.formData.accountType.$error }"
                    placeholder="Select account type"
                  />
                  <label>Account Type*</label>
                </div>
                <small
                  class="p-error mt-1 absolute p-d-block"
                  v-for="error of v$.formData.accountType.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </small>
              </div>
            </div>
          </div>

          <div
            class="form-row mb-3"
          >
            <label class="td-title">Currency</label>
            <div class="p-inputgroup">
              <div class="d-flex flex-column w-100">
                <div class="p-float-label">
                  <Dropdown
                    v-model="formData.currency"
                    :options="currencies"
                    optionLabel="description"
                    optionValue="id"
                    class="input-class"
                    :class="{ 'p-invalid': v$.formData.currency.$error }"
                    placeholder="Select currency"
                    filter
                  />
                  <label>Currency*</label>
                </div>
                <small
                  class="p-error mt-1 absolute p-d-block"
                  v-for="error of v$.formData.currency.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </small>
              </div>
            </div>
          </div>

          <div
            class="form-row mb-3"
          >
            <label class="td-title">Account Holder Name</label>
            <div class="p-inputgroup">
              <div class="d-flex flex-column w-100">
                <div class="p-float-label">
                  <InputText
                    v-model="formData.accountHolderName"
                    class="input-class"
                    :class="{
                      'p-invalid': v$.formData.accountHolderName.$error,
                    }"
                    placeholder="Enter account holder name"
                  />
                  <label>Account Holder Name*</label>
                </div>
                <small
                  class="p-error mt-1 absolute p-d-block"
                  v-for="error of v$.formData.accountHolderName.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </small>
              </div>
            </div>
          </div>

          <div
            class="form-row mb-3"
          >
            <label class="td-title">IBAN</label>
            <div class="p-inputgroup">
              <div class="d-flex flex-column w-100">
                <div class="p-float-label">
                  <InputText
                    v-model="formData.iban"
                    class="input-class"
                    :class="{
                      'p-invalid': v$.formData.iban.$error || ibanError,
                    }"
                    placeholder="Enter IBAN"
                    @input="validateIBAN"
                  />
                  <label>IBAN*</label>
                </div>
                <small
                  class="p-error mt-1 absolute p-d-block"
                  v-if="ibanError"
                  >{{ ibanError }}</small
                >
                <small
                  class="p-error mt-1 absolute p-d-block"
                  v-for="error of v$.formData.iban.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Card>

    <!-- Billing Information Section -->
    <Card class="profile-card">
      <template #header>
        <div class="card-header">
          <i class="fa-solid fa-credit-card mr-2"></i>Billing Information
        </div>
      </template>
      <template #content>
        <div class="card-content">
          <p class="subtitle mb-4">
            Specify Billing Information for Reward Payments
          </p>
          <div
            class="form-row mb-3"
          >
            <label class="td-title">Country</label>
            <div class="p-inputgroup">
              <div class="d-flex flex-column w-100">
                <div class="p-float-label">
                  <Dropdown
                    v-model="formData.country"
                    :options="countries"
                    optionLabel="description"
                    optionValue="id"
                    class="input-class"
                    :class="{ 'p-invalid': v$.formData.country.$error }"
                    placeholder="Select a country"
                    filter
                  />
                  <label>Country*</label>
                </div>
                <small
                  class="p-error mt-1 absolute p-d-block"
                  v-for="error of v$.formData.country.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </small>
              </div>
            </div>
          </div>

          <div
            class="form-row mb-3"
          >
            <label class="td-title">{{
              formData.accountType === '1' ? 'Full Name' : 'Company Name'
            }}</label>
            <div class="p-inputgroup">
              <div class="d-flex flex-column w-100">
                <div class="p-float-label">
                  <InputText
                    v-model="formData.billingName"
                    class="input-class"
                    :class="{ 'p-invalid': v$.formData.billingName.$error }"
                    :placeholder="
                      formData.accountType === '1'
                        ? 'Enter full name'
                        : 'Enter company name'
                    "
                  />
                  <label
                    >{{
                      formData.accountType === '1'
                        ? 'Full Name'
                        : 'Company Name'
                    }}*</label
                  >
                </div>
                <small
                  class="p-error mt-1 absolute p-d-block"
                  v-for="error of v$.formData.billingName.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </small>
              </div>
            </div>
          </div>

          <div
            class="form-row mb-3"
          >
            <label class="td-title">Billing Email</label>
            <div class="p-inputgroup">
              <div class="d-flex flex-column w-100">
                <div class="p-float-label">
                  <InputText
                    type="email"
                    v-model="formData.billingEmail"
                    class="input-class"
                    :class="{ 'p-invalid': v$.formData.billingEmail.$error }"
                    placeholder="Enter billing email"
                  />
                  <label>Billing Email*</label>
                </div>
                <small
                  class="p-error mt-1 absolute p-d-block"
                  v-for="error of v$.formData.billingEmail.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </small>
              </div>
            </div>
          </div>

          <div
            class="form-row mb-3"
          >
            <label class="td-title">City</label>
            <div class="p-inputgroup">
              <div class="d-flex flex-column w-100">
                <div class="p-float-label">
                  <Dropdown
                    v-model="formData.billingCity"
                    :options="cities"
                    optionLabel="description"
                    optionValue="id"
                    class="input-class"
                    :class="{ 'p-invalid': v$.formData.billingCity.$error }"
                    placeholder="Select a city"
                    filter
                    :disabled="!formData.country || cities.length === 0"
                  />
                  <label>City*</label>
                </div>
                <small
                  class="p-error mt-1 absolute p-d-block"
                  v-for="error of v$.formData.billingCity.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </small>
              </div>
            </div>
          </div>

          <div
            class="form-row mb-3"
          >
            <label class="td-title">Address</label>
            <div class="p-inputgroup">
              <div class="d-flex flex-column w-100">
                <div class="p-float-label">
                  <Textarea
                    v-model="formData.billingAddress"
                    class="input-class"
                    :class="{ 'p-invalid': v$.formData.billingAddress.$error }"
                    placeholder="Enter billing address"
                    rows="3"
                  />
                  <label>Address*</label>
                </div>
                <small
                  class="p-error mt-1 absolute p-d-block"
                  v-for="error of v$.formData.billingAddress.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </small>
              </div>
            </div>
          </div>

          <div
            class="form-row mb-3"
          >
            <label class="td-title">Postcode</label>
            <div class="p-inputgroup">
              <div class="d-flex flex-column w-100">
                <div class="p-float-label">
                  <InputText
                    v-model="formData.billingPostcode"
                    class="input-class"
                    :class="{ 'p-invalid': v$.formData.billingPostcode.$error }"
                    placeholder="Enter postcode"
                  />
                  <label>Postcode*</label>
                </div>
                <small
                  class="p-error mt-1 absolute p-d-block"
                  v-for="error of v$.formData.billingPostcode.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </small>
              </div>
            </div>
          </div>

          <div
            class="form-row mb-3"
            v-if="formData.accountType === '2'"
          >
            <label class="td-title">VAT Registration</label>
            <div class="p-inputgroup">
              <div class="d-flex flex-column w-100">
                <div class="p-float-label">
                  <Checkbox v-model="formData.isVatRegistered" :binary="true" />
                </div>
              </div>
            </div>
          </div>

          <div
            class="form-row mb-3"
            v-if="formData.accountType === '2'"
          >
            <label class="td-title">VAT/TAX Number</label>
            <div class="p-inputgroup">
              <div class="d-flex flex-column w-100">
                <div class="p-float-label">
                  <InputText
                    v-model="formData.billingTaxNumber"
                    class="input-class"
                    :class="{
                      'p-invalid': v$.formData.billingTaxNumber.$error,
                    }"
                    placeholder="Enter VAT/TAX number"
                    :disabled="!formData.isVatRegistered"
                  />
                  <label
                    >VAT/TAX Number<span v-if="formData.isVatRegistered"
                      >*</span
                    ></label
                  >
                </div>
                <small
                  class="p-error mt-1 absolute p-d-block"
                  v-for="error of v$.formData.billingTaxNumber.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Card>

    <div id="button" class="d-flex align-items-center justify-content-between">
      <CButton
      variant="faded"
      color="secondary"
        @click="$router.go(-1)"
        label="Cancel"
      />
      <CButton
        label="Save Changes"
        @click="handleSubmit"
        :loading="loading"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import useVuelidate from '@vuelidate/core'
import { required, email, minLength, requiredIf } from '@vuelidate/validators'
import { post, get } from '@/api/Services'
import { useStore } from 'vuex'
import Card from 'primevue/card'

const router = useRouter()
const toast = useToast()
const store = useStore()
const loading = ref(false)

const countries = ref([])
const cities = ref([])
const currencies = ref([])
const ibanError = ref('')
const accountTypes = [
  { name: 'Personal', value: '1' },
  { name: 'Corporate', value: '2' },
]

const formData = reactive({
  accountType: '1',
  country: null,
  currency: null,
  accountHolderName: '',
  iban: '',
  billingName: '',
  billingEmail: '',
  billingCity: null,
  billingAddress: '',
  billingPostcode: '',
  billingTaxNumber: '',
  isVatRegistered: false,
})

const rules = {
  formData: {
    accountType: { required },
    country: { required },
    currency: { required },
    accountHolderName: { required },
    iban: { required },
    billingTaxNumber: {
      required: requiredIf(
        () => formData.accountType === '2' && formData.isVatRegistered
      ),
    },
    billingName: { required },
    billingEmail: { required, email },
    billingCity: { required },
    billingAddress: { required, minLength: minLength(10) },
    billingPostcode: { required },
  },
}

const v$ = useVuelidate(rules, { formData })

const userParam = computed(() => store.state.account.userParam)

const selectedCountryName = computed(() => {
  const country = countries.value.find((c) => c.id === formData.country)
  return country ? country.description : ''
})

const validateIBAN = () => {
  const iban = formData.iban.replace(/\s/g, '').toUpperCase()

  // Basic IBAN format validation
  const ibanRegex =
    /^([A-Z]{2}[0-9]{2})(?=(?:[A-Z0-9]){9,30}$)((?:[A-Z0-9]{3,5}){2,7})([A-Z0-9]{1,3})?$/

  if (!ibanRegex.test(iban)) {
    ibanError.value = 'Invalid IBAN format'
    return false
  }

  // Move first 4 characters to end and convert letters to numbers
  const rearranged = (iban.substring(4) + iban.substring(0, 4))
    .split('')
    .map((char) => {
      const code = char.charCodeAt(0)
      return code >= 65 ? (code - 55).toString() : char
    })
    .join('')

  // Calculate checksum using mod-97
  let remainder = rearranged
  const chunkSize = 6
  let sum = 0

  for (let i = 0; i < remainder.length; i += chunkSize) {
    const chunk = remainder.slice(i, i + chunkSize)
    sum = parseInt(sum + chunk) % 97
  }

  if (sum !== 1) {
    ibanError.value = 'Invalid IBAN checksum'
    return false
  }

  ibanError.value = ''
  return true
}

const loadCurrencies = async () => {
  try {
    const response = await post('Commons/Executer', {
      sqlId: 49,
      parameters: JSON.stringify([]),
    })
    if (response) {
      currencies.value = response
    }
  } catch (error) {
    console.error('Error loading currencies:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Error loading currencies',
      life: 3000,
    })
  }
}

const loadCountries = async () => {
  try {
    const response = await get('/Countries')
    if (response) {
      countries.value = response
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error || 'Error loading countries',
      life: 3000,
    })
  }
}

const loadCities = async (countryId) => {
  try {
    const response = await get(`/Cities/${countryId}`)
    if (response) {
      cities.value = response
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error || 'Error loading cities',
      life: 3000,
    })
  }
}

const handleSubmit = async () => {
  v$.value.$touch()
  if (v$.value.$invalid) {
    return
  }

  loading.value = true
  try {
    const parameters = [
      // User related parameters from userParam
      {
        parameter_name: 'pin_selected_user_id',
        parameter_value: userParam.value.id,
      },
      {
        parameter_name: 'piv_firstname',
        parameter_value: userParam.value.firstname,
      },
      {
        parameter_name: 'piv_lastname',
        parameter_value: userParam.value.lastname,
      },
      {
        parameter_name: 'piv_linkedin_page',
        parameter_value: userParam.value.linkedin_page || '',
      },
      { parameter_name: 'piv_email', parameter_value: userParam.value.email },
      {
        parameter_name: 'piv_secondary_email',
        parameter_value: userParam.value.secondary_email || '',
      },
      {
        parameter_name: 'piv_phone',
        parameter_value: userParam.value.phone || '',
      },
      {
        parameter_name: 'piv_mobile',
        parameter_value: userParam.value.mobile || '',
      },
      {
        parameter_name: 'pin_gender_id',
        parameter_value: userParam.value.gender_id || -1,
      },
      {
        parameter_name: 'piv_title',
        parameter_value: userParam.value.title || '',
      },
      { parameter_name: 'piv_profile_picture_binary', parameter_value: '' },
      {
        parameter_name: 'piv_birth_date',
        parameter_value: userParam.value.birth_date || '',
      },
      {
        parameter_name: 'pin_location_country_id',
        parameter_value: userParam.value.location_country_id || -1,
      },
      {
        parameter_name: 'pin_location_city_id',
        parameter_value: userParam.value.location_city_id || -1,
      },
      {
        parameter_name: 'pin_external_user_job_notification',
        parameter_value: -1,
      },
      {
        parameter_name: 'piv_account_holder_name',
        parameter_value: formData.accountHolderName,
      },
      {
        parameter_name: 'pin_language_id',
        parameter_value: userParam.value.language_id,
      },
      {
        parameter_name: 'piv_external_user_job_notification_keywords',
        parameter_value: '',
      },
      { parameter_name: 'piv_additional_information', parameter_value: '' },
      { parameter_name: 'pin_style_color_id', parameter_value: -1 },

      // Unused bank account parameters (with default values)
      { parameter_name: 'pin_bank_account_type_id', parameter_value: -1 },
      { parameter_name: 'pin_account_country_id', parameter_value: -1 },
      { parameter_name: 'pin_account_currency_id', parameter_value: -1 },
      { parameter_name: 'pin_account_use_iban_or_accnum', parameter_value: 1 },
      { parameter_name: 'pin_account_sort_code', parameter_value: -1 },
      { parameter_name: 'pin_account_number', parameter_value: -1 },

      // International bank account parameters (from form inputs)
      {
        parameter_name: 'pin_int_bank_account_type_id',
        parameter_value: parseInt(formData.accountType),
      },
      {
        parameter_name: 'pin_int_account_country_id',
        parameter_value: formData.country,
      },
      {
        parameter_name: 'pin_int_account_currency_id',
        parameter_value: formData.currency,
      },
      {
        parameter_name: 'piv_int_account_holder_name',
        parameter_value: formData.accountHolderName,
      },
      {
        parameter_name: 'piv_int_payment_iban',
        parameter_value: formData.iban,
      },
      {
        parameter_name: 'pin_int_account_bic_or_swift_code',
        parameter_value: -1,
      },
      { parameter_name: 'piv_int_account_bank_name', parameter_value: '' },
      { parameter_name: 'piv_int_account_bank_address', parameter_value: '' },

      // Billing information parameters (from form inputs)
      {
        parameter_name: 'piv_ext_billing_company_name',
        parameter_value: formData.billingName,
      },
      {
        parameter_name: 'piv_ext_billing_company_email',
        parameter_value: formData.billingEmail,
      },
      {
        parameter_name: 'pin_ext_billing_company_location_country_id',
        parameter_value: formData.country,
      },
      {
        parameter_name: 'pin_ext_billing_company_location_city_id',
        parameter_value: formData.billingCity,
      },
      {
        parameter_name: 'piv_ext_billing_company_address',
        parameter_value: formData.billingAddress,
      },
      {
        parameter_name: 'piv_ext_billing_company_postcode',
        parameter_value: formData.billingPostcode,
      },
      {
        parameter_name: 'piv_ext_billing_company_tax_number',
        parameter_value:
          formData.accountType === '2' ? formData.billingTaxNumber : '',
      },
      { parameter_name: 'pin_is_allowed_unified_profile', parameter_value: 0 },
    ]

    const response = await post('Commons/Executer', {
      sqlId: 26,
      parameters: JSON.stringify(parameters),
    })

    if (response[0].status == 0) {
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: response[0].message,
        life: 3000,
      })

      //      router.go(-1)
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: response.message,
        life: 3000,
      })
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error || 'Error saving billing information',
      life: 3000,
    })
  } finally {
    loading.value = false
  }
}

// Watch for country changes
watch(
  () => formData.country,
  async (newValue) => {
    if (newValue) {
      await loadCities(newValue)
      formData.billingCity = null
    } else {
      cities.value = []
    }
  }
)

// Watch for account type changes to clear tax number when switching to personal
watch(
  () => formData.accountType,
  (newValue) => {
    if (newValue === '1') {
      formData.billingTaxNumber = ''
    }
  }
)

// Load initial data
onMounted(async () => {
  await Promise.all([loadCountries(), loadCurrencies()])

  // Populate form data from userParam if available
  if (userParam.value) {
    // Bank Account Information
    formData.accountType =
      userParam.value.int_bank_account_type_id?.toString() || '1'

    // Set country and load cities
    if (userParam.value.location_country_id) {
      formData.country = userParam.value.location_country_id
      await loadCities(formData.country)
    }

    // Set currency (using company default if available)
    formData.currency = userParam.value.company_default_currency_id || null

    // Account holder details
    formData.accountHolderName = userParam.value.account_holder_name || ''
    formData.iban = userParam.value.int_payment_iban || ''

    // Billing Information
    // For billing name, use company name for corporate accounts, full name for personal
    formData.billingName =
      formData.accountType === '2'
        ? userParam.value.company_name || ''
        : `${userParam.value.firstname} ${userParam.value.lastname}`.trim()

    formData.billingEmail = userParam.value.email || ''

    // Location information
    if (userParam.value.location_country_id) {
      formData.country = userParam.value.location_country_id
      await loadCities(formData.country)
      formData.billingCity = userParam.value.location_city_id || null
    }

    // Additional billing details
    formData.billingAddress = userParam.value.ext_billing_company_address || ''
    formData.billingPostcode =
      userParam.value.ext_billing_company_postcode || ''
    formData.billingTaxNumber =
      userParam.value.ext_billing_company_tax_number || ''
    formData.isVatRegistered = !!userParam.value.ext_billing_company_tax_number
  }
})
</script>

<style lang="scss" scoped>
.container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header-title {
  font-size: 32px;
  font-weight: 600;
  color: #1c1c1c;
}

.title {
  font-size: 26px;
  font-weight: 600;
  color: #1c1c1c;
}

.form-row {
  display: grid;
  grid-template-columns: 230px 1fr;
  align-items: center;
}

.td-title {
  color: #303952e5;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  margin: 0;
}

.input-class {
  width: 100%;
  border: 1px solid #dbdbdb;
  color: #303952e5;
  font-size: 16px;
  border-radius: 6px;
  font-weight: 400;
  background: none !important;
  border-radius: 4px !important;

  &:focus {
    border-color: #3262ff;
  }

  ::v-deep(.p-dropdown-label) {
    color: #303952e5 !important;
    font-size: 16px !important;
    font-weight: 400 !important;
  }
}

#button {
  position: sticky;
  background-color: #fff;
  width: 100%;
  bottom: 0;
  box-shadow: 0px 4px 4px 0px #0000000a;
  border-radius: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.choose-button-reverse {
  border-radius: 6px;
  background: #fff !important;
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  border: 1px solid #fc6c2a !important;
  transition: all 320ms;
  color: #fc6c2a;

  &:hover {
    background: #fc6c2a !important;
    color: #fff;
  }
}

.choose-button {
  border-radius: 6px;
  background: #fc6c2a !important;
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  border: 1px solid #fc6c2a !important;
  transition: all 320ms;

  &:hover {
    background: #fff !important;
    color: #fc6c2a;
  }

  &:disabled {
    color: #fff !important;
    opacity: 0.5;
  }
}

.subtitle {
  color: #666;
  font-size: 16px;
  margin-top: -12px;
}

.required-star {
  color: #ff0000;
  margin-left: 4px;
}

/* New styles imported from About.vue */
.profile-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 1.5rem;
  overflow: hidden;
  border: none;
  transition: transform 0.2s, box-shadow 0.2s;
}

.profile-card ::v-deep(.p-card-content) {
  padding: 0.5rem;
}

.card-header {
  font-size: 1.25rem;
  font-weight: 600;
  color: #103660;
  background-clip: #f1f1f1;
  display: flex;
  align-items: center;
  border: 0;
  i {
    font-size: 14px;
  }
}

.card-content {
  padding: 1.5rem;
}

/* Responsive styles */
@media (max-width: 768px) {
  .container {
    padding: 15px;
  }
  
  .header-title {
    font-size: 24px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
    grid-gap: 10px;
  }
  
  .td-title {
    display: none; /* Hide the labels on mobile */
  }
  
  .card-content {
    padding: 1rem;
  }
  
  .card-header {
    font-size: 1.1rem;
  }
  
  #button {
    padding: 10px;
  }
  
  .choose-button, .choose-button-reverse {
    font-size: 14px;
    padding: 0.5rem 1rem;
  }
}

@media (max-width: 480px) {
  .header-title {
    font-size: 20px;
  }
  
  .td-title {
    font-size: 14px;
  }
  
  .subtitle {
    font-size: 14px;
  }
  
  .card-content {
    padding: 0.8rem;
  }
  
  #button {
    flex-direction: column;
    gap: 10px;
    & > * {
      width: 100%;
    }
  }
  
  .choose-button, .choose-button-reverse {
    width: 100%;
  }
}
</style>
